import { Component, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { Endpoint, HelpIndex, SettingsType } from '../../../../models/enums';
import { CellTemplateType, GenericColDef, GridContext, GridRowSelectionType, SortByFilter } from '../../../../models/grid-context';
import * as _ from 'underscore';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { RestService } from '../../../../services/rest/rest.service';
import { RowNode, SelectionChangedEvent } from 'ag-grid-community';
import { Service } from '../../../../models/service';
import { CommonService } from '../../../../services/common/common.service';
import { isNullOrUndefined } from '../../../../models/utils';
import { CreateEditEntityModalComponent } from '../../create-edit-entity-modal/create-edit-entity-modal.component';
import { ActionType, ObjectActionListType, PermissionSet, Permissions } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-services',
    templateUrl: './services.component.html',
    styleUrls: ['./services.component.scss']
})
export class ServicesComponent implements SettingsNavigationContentComponent, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;
    @ViewChild('realmnodes', { static: true }) realmNodesGrid: GenericGridComponent;
    @ViewChild('organdprojects', { static: true }) orgsAndProjectsGrid: GenericGridComponent;

    automationId = 'services';
    realmNodesAutoId = 'realm-nodes';
    orgsAndProjectsAutoId = 'organizations-and-projects';
    isDirty: boolean;
    validChanges = true;
    gridName = 'Services';
    gridNameRealmNodes = 'Services Realm Nodes';
    services: Array<any>;
    realmNodes: Array<any>;
    orgsAndProjects: Array<any>;
    organizationsAndProjects: Array<any>;
    selectedService: any;
    serviceRealmNodes: Array<any>;
    serviceRealmNodeNames: Array<string>;
    isReselectService: boolean;
    reselectService: any;
    noRealmNodesSelected: boolean;
    realmNodesGridColDefs: any;
    hasServiceAddEditPermissions: boolean;

    isBusy = false;
    isBusyMessage = 'Loading Services ...';
    helpIndex = HelpIndex.sys_services_summary_dlg;

    gridContext = new GridContext({
        sortBy: 'serviceName',
        sortAscending: true
    });

    gridContextRealmNodes = new GridContext({
        sortBy: 'name',
        sortAscending: true
    });

    gridContextOrgsAndProjects = new GridContext({
        sortAscending: true,
        sortBy: 'orgName',
    });

    requestInfo = {
        endpoint: Endpoint.realmManager_listServices,
        responseName: 'services',
        data: { startIndex: 0, count: 100, descending: false, sortByFilter: SortByFilter.NAME, contextHandle: 'super_system_customer' }
    };

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Name',
            field: 'serviceName',
            lockVisible: true,
            automationId: this.automationId,
            sortBy: 'serviceName',
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId,
        }, {
        automationId: this.automationId,
        cellTemplateType: CellTemplateType.contextMenu,
        minWidth: 30, maxWidth: 30, width: 30,
        suppressSizeToFit: true
    }
    );

    colDefsRealNodesWithPermission = new Array<GenericColDef>(
        {
            colId: 'checkbox',
            automationId: this.realmNodesAutoId,
            minWidth: 36, maxWidth: 36, width: 36,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            suppressSizeToFit: true,
        },
        {
            headerName: 'Realm Node Name',
            field: 'name',
            automationId: this.realmNodesAutoId,
            suppressMovable: true,
            sortBy: 'name',
        },
        {
            headerName: 'Realm Node Type',
            field: 'analyticNodeMode',
            automationId: this.realmNodesAutoId,
            suppressMovable: true
        }
    );

    colDefsRealNodesWithoutPermission = new Array<GenericColDef>(
        {
            headerName: '',
            field: 'isEnabled',
            minWidth: 36, maxWidth: 36, width: 36,
            automationId: this.realmNodesAutoId,
            suppressSizeToFit: true,
            cellTemplateType: CellTemplateType.disabledCheckbox,
        },
        {
            headerName: 'Realm Node Name',
            field: 'name',
            automationId: this.realmNodesAutoId,
            suppressMovable: true,
            sortBy: 'name',
        },
        {
            headerName: 'Realm Node Type',
            field: 'analyticNodeMode',
            automationId: this.realmNodesAutoId,
            suppressMovable: true
        }
    );

    colDefsOrgsAndProjects = new Array<GenericColDef>(
        {
            headerName: 'Organization',
            field: 'orgName',
            automationId: this.orgsAndProjectsAutoId,
            suppressMovable: true,
            sortBy: 'orgName',
        },
        {
            headerName: 'Project',
            field: 'name',
            automationId: this.orgsAndProjectsAutoId,
            suppressMovable: true,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        }
    );

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Edit ...',
            iconClass: 'fa-pencil',
            iconColor: 'color-blue-01',
            automationId: 'services-edit-service',
            click: (item) => this.addEditNewService(item),
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.SERVICES, permissionActions: [ActionType.EDIT] })]
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: 'services-delete-service',
            click: (item) => this.deleteService(item),
            enabled: (item) => (!item?.serviceInUse && !item?.defaultService),
            tooltipFunction: (item: any) => (item?.serviceInUse && !item?.defaultService) ? 'This service is in use and cannot be deleted.' : (item?.defaultService ? 'The Reef ECA Default Service cannot be deleted.' : ''),
            tooltipAlwaysOn: true,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.SERVICES, permissionActions: [ActionType.DELETE] })]
        })
    ];

    rowClassRules = {
        'no-click-row': (param) => {
            if (this.realmNodes?.length === 1) {
                return true;
            }
            return false;
        }
    };

    realmNodesSelection = Permissions.checkUserPermission(ObjectActionListType.SERVICES, ActionType.EDIT) ? GridRowSelectionType.multipleCheckboxWithRowClickSelection : GridRowSelectionType.none;
    servicesSelection = GridRowSelectionType.single;

    constructor(private restService: RestService, private commonService: CommonService,) { }

    responseCallback = (response: any) => {
        if (response?.services) {
            response.services = _.map(response?.services, service => {
                return new Service(service);
            });
            this.services = response.services;
            this.commonService.keepGridSpinner = true;
            this.realmNodesGrid.isBusy = true;
            this.orgsAndProjectsGrid.isBusy = true;
            this.realmNodesGrid.isBusyMessage = 'Loading Realm Nodes ...';
            this.orgsAndProjectsGrid.isBusyMessage = 'Loading Organizations and Projects ...';
        }
    }

    responseArrayCallback = async (value: any) => {
        value.serviceInUse = await this.checkServiceInUse(value);
        value.defaultService = value?.serviceName?.includes("Digital Reef Default");
        return value;
    }

    loadDataFinishedCallback = () => {
        this.retryOnGridReady(this.services);
    }

    retryOnGridReady(servicesResponse: any): void {
        const servicesRows = this.genericGrid?.getRows();
        if (this.realmNodesGrid && servicesRows.length > 0) {
            this.selectedService = servicesRows[0]?.data;
            if (this.isReselectService) {
                const serviceIndex = servicesRows.findIndex(obj => obj.data.handle === this.reselectService?.data.handle);
                this.genericGrid?.selectNode(servicesRows[serviceIndex !== -1 ? serviceIndex : 0]);
                this.isReselectService = false;
                this.reselectService = null;
            } else {
                this.genericGrid?.selectNode(servicesRows[0]);
            }
        } else if (servicesRows.length === 0 && servicesResponse.length > 0) {
            setTimeout(() => {
                this.retryOnGridReady(servicesResponse);
            }, 500);
        } else {
            this.genericGrid.isBusy = false;
            this.realmNodesGrid.isBusy = false;
            this.orgsAndProjectsGrid.isBusy = false;
            this.commonService.keepGridSpinner = false;
        }
    }

    onSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api && this.genericGrid.gridApi) {
            this.realmNodesGrid.gridCheckedItems = [];

            this.selectedService = event.api.getSelectedRows()[0];
            if (this.selectedService && this.selectedService.handle) {
                this.listRealmNodes(this.selectedService);
                this.listOrgsAndProjects(this.selectedService);
            } else {
                this.realmNodesGrid.isBusy = false;
                this.orgsAndProjectsGrid.isBusy = false;
            }
            this.genericGrid.isBusy = false;
            this.commonService.keepGridSpinner = false;
        }
    }

    realNodesOnSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api && this.realmNodesGrid.gridApi) {
            const selectedNodes = event.api.getSelectedRows();
            this.noRealmNodesSelected = selectedNodes.length === 0;
        }
    }

    listRealmNodes(selectedService: any): void {
        this.realmNodesGrid.setData([]);
        this.realmNodes = [];
        this.realmNodesGrid.isBusy = true;
        this.realmNodesGrid.isBusyMessage = 'Loading Realm Nodes ...';
        this.restService.post(Endpoint.realmManager_listNodes, { systemScope: true, descending: false, sortByFilter: SortByFilter.NAME, contextHandle: 'super_system_customer' }).then((response) => {
            this.realmNodes = (response?.nodes || [])
                .map(node => {
                    node.analyticNodeMode = node.analyticNodeMode === 'OCR' ? 'OCR' : node.analyticNodeMode.charAt(0).toUpperCase() + node.analyticNodeMode.slice(1).toLowerCase();
                    const serviceNodes = selectedService?.serviceNodes?.toLowerCase().split(',');
                    node.isEnabled = serviceNodes?.includes(node.name?.toLowerCase());
                    return node;
                })
            if (response?.nodes?.length > 1 && ((response?.nodes[0]).nodeType !== 'FULL' || response?.nodes[0].nodeType !== 'ANALYTIC')) {
                this.realmNodes = this.realmNodes.filter(node => node.nodeType === 'FULL' || node.nodeType === 'ANALYTIC');
            }

            if (this.realmNodes?.length === 1) {
                this.realmNodesGrid.setColumnDefs(this.colDefsRealNodesWithoutPermission);
            }

            this.realmNodesGrid.setData(_.sortBy(this.realmNodes, 'name'));
            const nodes = this.realmNodesGrid?.getRows();
            const serviceNodes = selectedService.serviceNodes?.toLowerCase().split(',');
            const hiddenServiceNode = response?.nodes?.filter(node => node.nodeType !== 'FULL' && node.nodeType !== 'ANALYTIC').map(node => node.name);
            this.serviceRealmNodeNames = hiddenServiceNode?.length === 1 ? serviceNodes?.filter(node => node !== hiddenServiceNode[0]) : serviceNodes;
            _.each(nodes, (node) => {
                if (serviceNodes?.includes(node.data?.name?.toLowerCase())) {
                    this.realmNodesGrid.selectNode(node);
                }
            });

            this.serviceRealmNodes = JSON.parse(JSON.stringify(this.realmNodesGrid?.gridApi?.getSelectedRows()));
            this.realmNodesGrid.isBusy = false;
        }, error => {
            this.realmNodesGrid.isBusy = false;
        });
    }

    listOrgsAndProjects(selectedService: any): void {
        this.orgsAndProjects = [];
        this.orgsAndProjectsGrid.isBusy = true;
        this.orgsAndProjectsGrid.isBusyMessage = 'Loading Organizations & Projects ...';
        this.restService.post(Endpoint.serviceManager_listProjectsForService,
            { count: 0, handle: selectedService.handle, startIndex: 0, descending: false, sortByFilter: SortByFilter.NAME, contextHandle: 'super_system_customer', systemScope: true }).then((response) => {
                if (response) {
                    response.projects = response.projects?.length > 0 ? response.projects.filter(p => p.name !== 'organizationdefault') : response.projects;    // filter out default project from an org
                    this.orgsAndProjects = _.map(response?.projects, entry => {
                        if (!isNullOrUndefined(entry.description) && entry.description !== '') {
                            entry.name = entry.name + ' - ' + entry.description;
                            entry.tooltip = entry.name;
                        }
                        return entry;
                    });
                    this.orgsAndProjectsGrid.setData(_.sortBy(this.orgsAndProjects, 'orgName'));
                }
                this.orgsAndProjectsGrid.isBusy = false;
            }, () => {
                this.orgsAndProjectsGrid.isBusy = false;
            });
    }

    saveChanges(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.saveRealmNodes()
        });
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.commonService.keepGridSpinner = false;
        this.hasServiceAddEditPermissions = Permissions.checkUserPermission(ObjectActionListType.SERVICES, ActionType.EDIT);
        this.realmNodesGridColDefs = this.hasServiceAddEditPermissions ? this.colDefsRealNodesWithPermission : this.colDefsRealNodesWithoutPermission;
        return;
    }

    addEditNewService(item): void {
        if (item) {
            const request = this.createRequest();
            item.serviceExpressNodes = this.isDisabled() ? this.serviceRealmNodes.map(e => e.name) : request.serviceExpressNodes;
            item.serviceOcrNodes = request.serviceOcrNodes;
            item.serviceRealmNodes = request.serviceRealmNodes;
        }
        this.reselectService = this.genericGrid?.gridApi?.getSelectedNodes()[0];
        this.commonService.openModal(CreateEditEntityModalComponent, { modalData: item ? item : new Service() }).then((value) => {
            if (value.modalResult) {
                if (item) {
                    this.isReselectService = true;
                    this.reselectService = this.genericGrid?.gridApi?.getSelectedNodes()[0];
                } else if (value?.modalData?.service) {
                    this.isReselectService = true;
                    this.reselectService = { data: value.modalData.service };
                }
                this.genericGrid.loadData();
            }
        });
    }

    async checkServiceInUse(item: any): Promise<boolean> {
        let serviceInUse = false;
        try {
            const response = await this.restService.post(Endpoint.serviceManager_listProjectsForService,
                { count: 0, handle: item.handle, startIndex: 0, contextHandle: 'super_system_customer', systemScope: true });

            serviceInUse = response?.projects?.length > 0;
        } catch (error) {
            console.error(error);
        }
        return serviceInUse;
    }

    deleteService(item: any): void {
        this.restService.post(Endpoint.serviceManager_listProjectsForService,
            { count: 0, handle: item.handle, startIndex: 0, contextHandle: 'super_system_customer', systemScope: true }).then((response) => {
                const serviceInUse = response?.projects?.length > 0;
                if (!serviceInUse) {
                    this.commonService.openModal(DeleteEntityModalComponent, { modalData: item }).then(response => {
                        if (response.modalResult) {
                            setTimeout(() => {
                                this.isReselectService = false;
                                this.genericGrid.loadData();
                            }, 2000);
                        }
                    });
                }
            });
    }

    getSelectedService(): any {
        const selectedService = this.genericGrid?.gridApi?.getSelectedRows();
        return selectedService[0];
    }

    sameRealmNodesArray(a1: string[], a2: string[]): boolean {
        return a1?.length === a2?.length && a1?.every(element => a2?.includes(element));
    }

    isDisabled(): boolean {
        const selectedRows = this.realmNodesGrid?.gridApi?.getSelectedRows();
        const selectedNodeNames = selectedRows?.map(r => r.name.toLowerCase());
        const isDisabled = (selectedRows?.length === this.serviceRealmNodes?.length && this.sameRealmNodesArray(selectedNodeNames, this.serviceRealmNodeNames)) || this.isBusy || (isNullOrUndefined(selectedRows) || isNullOrUndefined(this.serviceRealmNodes));
        this.isDirty = !isDisabled || this.noRealmNodesSelected;
        return isDisabled || this.realmNodes?.length === 1;
    }

    createRequest(): any {
        const selectedRows = this.realmNodesGrid?.gridApi?.getSelectedRows();
        const request = {
            serviceName: this.selectedService.serviceName,
            serviceDescription: this.selectedService.description,
            serviceExpressNodes: [],
            serviceOcrNodes: [],
            serviceRealmNodes: [],
            requestHandle: this.selectedService.handle,
            contextHandle: 'super_system_customer'
        }

        const nodes = selectedRows;

        _.each(nodes, (data) => {
            if (data.analyticNodeMode == 'EXPRESS') {
                request.serviceExpressNodes.push(data.name);
            }
            else if (data.analyticNodeMode == 'OCR') {
                request.serviceOcrNodes.push(data.name);
            }
            else {
                request.serviceRealmNodes.push(data.name)
            }
        });

        return request;
    }

    saveRealmNodes(): void {
        this.restService.post(Endpoint.serviceManager_updateService, this.createRequest()).then((response) => {
            if (response) {
                setTimeout(() => {
                    this.isReselectService = true;
                    this.reselectService = this.genericGrid?.gridApi?.getSelectedNodes()[0];
                    this.genericGrid.loadData();
                    this.isDirty = false;
                });
            }
        });
    }

    discardChanges(): void {
        this.discardRealmNodesChanges();
    }

    discardRealmNodesChanges(): void {
        this.isDirty = false;
        this.listRealmNodes(this.selectedService);
    }

    openHelp = () => this.commonService.openHelp(HelpIndex.sys_services_summary_dlg);

    ngOnDestroy(): void {
        this.commonService.keepGridSpinner = false;
    }
}